import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTasks } from '@/contexts/TaskContext';
import { Plus, Pencil, Trash2, AlertTriangle, Archive, CalendarDays, Filter, RotateCcw, CheckCircle, Users, FileSpreadsheet } from 'lucide-react';
import { DIVISION_NAMES, getJobsForDivision, OTHER_JOB } from '@/constants/divisions';
import JobWorkCard from '@/components/JobWorkCard';
import JobCardSteps from '@/components/JobCardSteps';
import WorkBriefImportModal from '@/components/WorkBriefImportModal';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks, isWithinInterval } from 'date-fns';
import { istNow, istTodayKey, isThisWeekIST } from '@/utils/istClock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RASheet = () => {
  const { user } = useAuth();
  const [workBriefOpen, setWorkBriefOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [extensionRequests, setExtensionRequests] = useState([]);
  const [extensionRequestsOpen, setExtensionRequestsOpen] = useState(false);
  const [customApprovalDate, setCustomApprovalDate] = useState({});

  const fetchExtensionRequests = async () => {
    try {
      const res = await axios.get(`${API}/extension-requests/pending`);
      setExtensionRequests(res.data.requests || []);
    } catch (error) {
      console.error('Error fetching extension requests:', error);
    }
  };

  useEffect(() => {
    fetchExtensionRequests();
  }, []);

  const handleExtensionDecision = async (taskId, decision, approvedDate) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/extension-decision`, {
        decision,
        approvedDate: approvedDate || null
      });
      toast.success(decision === 'approve' ? 'Extension approved' : 'Extension rejected');
      fetchExtensionRequests();
      refreshTasks();
    } catch (error) {
      console.error('Error deciding extension:', error);
      toast.error('Failed to process request');
    }
  };
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  const handleBulkArchive = async (taskList) => {
    if (!window.confirm(`Archive ${selectedTaskIds.size} selected task(s)?`)) return;
    try {
      await Promise.all([...selectedTaskIds].map(id =>
        axios.put(`${API}/tasks/${id}`, {
          archived: true,
          archivedGlobally: true,
          archivedDate: new Date().toISOString()
        })
      ));
      toast.success(`${selectedTaskIds.size} task(s) archived`);
      setSelectedTaskIds(new Set());
      refreshTasks();
    } catch (error) {
      console.error('Error bulk archiving:', error);
      toast.error('Failed to archive some tasks');
    }
  };
  const handleBulkDelete = async () => {
    const idsToDelete = [...selectedTaskIds];
    if (!window.confirm(`Delete ${idsToDelete.length} selected task(s)?`)) return;
    try {
      await Promise.all(idsToDelete.map(id => axios.delete(`${API}/tasks/${id}`)));
      setSelectedTaskIds(new Set());
      refreshTasks();
      toast.success(`${idsToDelete.length} task(s) deleted`, {
        description: 'You have 20 minutes to undo this.',
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await Promise.all(idsToDelete.map(id => axios.post(`${API}/tasks/${id}/restore`)));
              toast.success('Tasks restored');
              refreshTasks();
            } catch {
              toast.error('Failed to restore some tasks');
            }
          }
        },
        duration: 15000
      });
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete some tasks');
    }
  };
  // Read from the global TaskContext (single source of truth).
  // Uses full `tasks` (incl. archived) so the "Show Archived" toggle still works.
  const { tasks, teamMembers, accounts, loading, refreshTasks } = useTasks();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const specificMonth = selectedMonth && selectedYear ? `${selectedYear}-${selectedMonth}` : '';
  const [showArchived, setShowArchived] = useState(false);
  const [accountFilter, setAccountFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    deliverable: '',
    job: '',
    taskType: '',
    status: 'pending',
    dueDate: '',
    accountId: '',
    week: '',
    assignedTo: '',
    accountable: [],
    accountName: '',
    priority: 'med',
    instructions: '',
    links: '',
    responsible: [],
    consulted: [],
    informed: [],
    assignedBy: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...formData,
        links: formData.links ? formData.links.split(',').map(l => l.trim()) : []
      };

      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, taskData);
        toast.success('Task updated successfully', {
          description: 'Changes synced across all views'
        });
      } else {
        await axios.post(`${API}/tasks`, taskData);
        toast.success('Task created successfully', {
          description: 'Synced to team member task sheets'
        });
      }

      setDialogOpen(false);
      setEditingTask(null);
      resetForm();
      refreshTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save task');
    }
  };

  const toggleMemberInRole = (role, memberId) => {
    setFormData(prev => {
      const currentList = prev[role] || [];
      if (currentList.includes(memberId)) {
        return { ...prev, [role]: currentList.filter(id => id !== memberId) };
      } else {
        return { ...prev, [role]: [...currentList, memberId] };
      }
    });
  };
  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      deliverable: task.deliverable || '',
      job: task.job || '',
      taskType: task.taskType || '',
      status: task.status || 'pending',
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      accountId: task.accountId || '',
      week: task.week || 'Week 1',
      assignedTo: task.assignedTo || '',
      accountable: task.accountable || [],
      accountName: task.accountName || '',
      priority: task.priority || 'med',
      instructions: task.instructions || '',
      links: task.links ? task.links.join(', ') : '',
      responsible: task.responsible || [],
      consulted: task.consulted || [],
      informed: task.informed || [],
      assignedBy: task.assignedBy || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      refreshTasks();
      toast.success('Task deleted', {
        description: 'You have 20 minutes to undo this.',
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(`${API}/tasks/${taskId}/restore`);
              toast.success('Task restored');
              refreshTasks();
            } catch {
              toast.error('Failed to restore task');
            }
          }
        },
        duration: 15000
      });
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      deliverable: '',
      job: '',
      taskType: '',
      status: 'pending',
      dueDate: '',
      accountId: '',
      week: '',
      assignedTo: '',
      accountable: [],
      accountName: '',
      priority: 'med',
      instructions: '',
      links: '',
      responsible: [],
      consulted: [],
      informed: [],
      assignedBy: ''
    });
  };

  // Archive handlers (GLOBAL - Admin from RA Sheet page)
  const handleArchive = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        archived: true,
        archivedGlobally: true,
        archivedDate: new Date().toISOString()
      });
      toast.success('Task archived globally');
      refreshTasks();
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  const handleUnarchive = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        archived: false,
        archivedGlobally: false,
        archivedDate: null
      });
      toast.success('Task restored globally');
      refreshTasks();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  // Filter tasks by date
  const filterTasksByDate = (taskList) => {
    if (dateFilter === 'all' || dateFilter === 'done') return taskList;
    
    return taskList.filter(task => {
      if (!task.dueDate) return dateFilter === 'no-date';
      
      try {
        const dueDate = parseISO(task.dueDate);
        const now = istNow();
        
        switch (dateFilter) {
          case 'today': {
            // CR-15 (updated): Start Date is today (IST); a task without a Start Date uses its Due Date
            if (['Done', 'done'].includes(task.status)) return false;
            return format(task.startDate ? parseISO(task.startDate) : dueDate, 'yyyy-MM-dd') === istTodayKey();
          }
          case 'this-week':
            return isThisWeekIST(dueDate, { weekStartsOn: 1 });
          case 'last-week': {
            const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
          }
          case 'next-week': {
            const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
            const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
            return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd });
          }
          case 'overdue':
            return task.isLagging;
          case 'specific': {
            if (!specificDate) return true;
            const pickedDay = parseISO(specificDate);
            const effectiveStartSpecific = task.startDate || task.assignedDate;
            if (effectiveStartSpecific) {
              const startDay = new Date(parseISO(effectiveStartSpecific)); startDay.setHours(0,0,0,0);
              const dueDay = new Date(dueDate); dueDay.setHours(0,0,0,0);
              return startDay <= pickedDay && pickedDay <= dueDay;
            }
            return format(dueDate, 'yyyy-MM-dd') === specificDate;
          }
          case 'specific-month':
            if (!specificMonth) return true;
            return format(dueDate, 'yyyy-MM') === specificMonth;
          case 'no-date':
            return false;
          default:
            return true;
        }
      } catch {
        return false;
      }
    });
  };

  // Get filtered tasks (by date and archive status)
  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // Filter by archive status
    if (!showArchived) {
      filtered = filtered.filter(t => !t.archived && !t.archivedGlobally);
      if (dateFilter === 'done') {
        filtered = filtered.filter(t => ['Done', 'done'].includes(t.status));
      } else {
        filtered = filtered.filter(t => !['Done', 'done'].includes(t.status));
      }
    } else {
      filtered = filtered.filter(t => t.archived || t.archivedGlobally);
    }
    
    // Filter by client/account
    if (accountFilter !== 'all') {
      filtered = filtered.filter(t => t.accountId === accountFilter);
    }

    // Filter by date
    filtered = filterTasksByDate(filtered);
    
    // Sort: today first, then each later date in order; past due dates after that; no due date last.
    // Within the same date: priority (High, Medium, Low), then title A-Z.
    const sortTodayKey = istTodayKey();
    const sortDayKey = (d) => { try { return d ? format(parseISO(d), 'yyyy-MM-dd') : ''; } catch { return ''; } };
    const sortGroup = (k) => (!k ? 2 : k >= sortTodayKey ? 0 : 1);
    const sortPriority = { high: 0, med: 1, medium: 1, low: 2 };
    return [...filtered].sort((a, b) => {
      const ak = sortDayKey(a.dueDate), bk = sortDayKey(b.dueDate);
      const g = sortGroup(ak) - sortGroup(bk);
      if (g !== 0) return g;
      if (ak !== bk) return ak < bk ? -1 : 1;
      const p = (sortPriority[a.priority] ?? 3) - (sortPriority[b.priority] ?? 3);
      if (p !== 0) return p;
      return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
    });
  };

  // Count stats
  const activeTasksCount = tasks.filter(t => !t.archived && !t.archivedGlobally && !['Done', 'done'].includes(t.status)).length;
  const doneTasksCount = tasks.filter(t => !t.archived && !t.archivedGlobally && ['Done', 'done'].includes(t.status)).length;
  const archivedTasksCount = tasks.filter(t => t.archived || t.archivedGlobally).length;
  const todayTasksCount = tasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally || ['Done', 'done'].includes(t.status)) return false;
    try {
      const due = parseISO(t.dueDate);
      return format(t.startDate ? parseISO(t.startDate) : due, 'yyyy-MM-dd') === istTodayKey(); // CR-15 (updated): start date today (IST), else due today
    } catch {
      return false;
    }
  }).length;
  const overdueTasksCount = tasks.filter(t => !t.archived && !t.archivedGlobally && t.isLagging).length;
  const thisWeekTasksCount = tasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      return isThisWeekIST(parseISO(t.dueDate), { weekStartsOn: 1 });
    } catch {
      return false;
    }
  }).length;
  const lastWeekTasksCount = tasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      const dueDate = parseISO(t.dueDate);
      const now = istNow();
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
    } catch {
      return false;
    }
  }).length;
  const nextWeekTasksCount = tasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      const dueDate = parseISO(t.dueDate);
      const now = istNow();
      const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd });
    } catch {
      return false;
    }
  }).length;

  const getStatusBadge = (status) => {
    const badges = {
      done: 'bg-green-100 text-green-700 border-green-300',
      ongoing: 'bg-blue-100 text-blue-700 border-blue-300',
      pending: 'bg-gray-100 text-gray-700 border-gray-300',
      'on-hold': 'bg-red-100 text-red-700 border-red-300'
    };
    return badges[status] || badges.pending;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: 'bg-red-100 text-red-700 border-red-300 font-semibold',
      med: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      low: 'bg-green-100 text-green-700 border-green-300'
    };
    return badges[priority] || badges.med;
  };

  const getRowClassName = (task) => {
    if (task.isLagging) {
      return 'bg-red-50 border-l-4 border-l-red-500 hover:bg-red-100';
    }
    if (task.priority === 'high') {
      return 'bg-red-50 border-l-4 border-l-red-400 hover:bg-red-100';
    }
    if (task.priority === 'med') {
      return 'bg-yellow-50 border-l-4 border-l-yellow-400 hover:bg-yellow-100';
    }
    if (task.status === 'Done') {
      return 'bg-green-50 hover:bg-green-100';
    }
    return 'hover:bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Get filtered and sorted tasks
  const sortedTasks = getFilteredTasks();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="ra-sheet-page"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">Task Management</h1>
          <p className="text-muted-foreground mt-1">View and manage all tasks - sorted by priority and due date</p>
        </div>
        {user?.role === 'admin' && (
          <Button variant="outline" onClick={() => setWorkBriefOpen(true)} data-testid="import-work-brief-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Import Work Brief
          </Button>
        )}
        <WorkBriefImportModal open={workBriefOpen} onOpenChange={setWorkBriefOpen} onSuccess={refreshTasks} />
        {/* Edit Task Dialog - only opens when editing */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="task-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title" className="text-sm font-semibold">Task Title *</Label>
                  <Input
                    id="title"
                    data-testid="task-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    readOnly={!!formData.job && formData.job !== OTHER_JOB}
                    className={`mt-1.5 ${(formData.job && formData.job !== OTHER_JOB) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                    placeholder={(formData.job && formData.job !== OTHER_JOB) ? 'Filled from the job you selected' : 'Enter task title'}
                  />
                </div>
                <div>
                  <Label htmlFor="accountId" className="text-sm font-semibold">Account/Client *</Label>
                  <Select value={formData.accountId} onValueChange={(value) => setFormData({ ...formData, accountId: value })}>
                    <SelectTrigger data-testid="task-account-select" className="mt-1.5 bg-white">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {false && (
                <div>
                  <Label htmlFor="week" className="text-sm font-semibold">Week</Label>
                  <Select value={formData.week} onValueChange={(value) => setFormData({ ...formData, week: value })}>
                    <SelectTrigger className="mt-1.5 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="Week 1">Week 1</SelectItem>
                      <SelectItem value="Week 2">Week 2</SelectItem>
                      <SelectItem value="Week 3">Week 3</SelectItem>
                      <SelectItem value="Week 4">Week 4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}
                <div>
                  <Label htmlFor="deliverable" className="text-sm font-semibold">Division *</Label>
                  <Select
                    value={formData.deliverable}
                    onValueChange={(value) => setFormData({ ...formData, deliverable: value, job: '', title: '' })}
                  >
                    <SelectTrigger className="mt-1.5 bg-white" data-testid="division-select">
                      <SelectValue placeholder="Select a division" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {formData.deliverable && !DIVISION_NAMES.includes(formData.deliverable) && (
                        <SelectItem value={formData.deliverable}>{formData.deliverable} (old value)</SelectItem>
                      )}
                      {DIVISION_NAMES.map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-4">
                    <Label className="text-sm font-semibold">Job (work card)</Label>
                    <Select
                      value={formData.job}
                      onValueChange={(value) => setFormData({ ...formData, job: value, title: value === OTHER_JOB ? '' : value })}
                      disabled={!formData.deliverable}
                    >
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="job-select">
                        <SelectValue placeholder={formData.deliverable ? 'Select a job' : 'Select a division first'} />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-80">
                        {formData.job && !getJobsForDivision(formData.deliverable).includes(formData.job) && (
                          <SelectItem value={formData.job}>{formData.job}</SelectItem>
                        )}
                        {getJobsForDivision(formData.deliverable).map((job) => (
                          <SelectItem key={job} value={job}>{job}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <JobCardSteps division={formData.deliverable} job={formData.job} defaultOpen className="mt-3" />
                  </div>
                </div>
                {false && (
                <div>
                  <Label htmlFor="taskType" className="text-sm font-semibold">Task Type</Label>
                  <Input
                    id="taskType"
                    data-testid="task-type-input"
                    value={formData.taskType}
                    onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
                    className="mt-1.5 bg-white"
                    placeholder="e.g., Content Creation"
                  />
                </div>
                )}
                <div>
                  <Label className="text-sm font-semibold">Assigned By</Label>
                  <Select value={formData.assignedBy} onValueChange={(value) => setFormData({ ...formData, assignedBy: value })}>
                    <SelectTrigger className="mt-1.5 bg-white">
                      <SelectValue placeholder="Select who assigned this" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {teamMembers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="accountName" className="text-sm font-semibold">Account/Project</Label>
                  <Input
                    id="accountName"
                    data-testid="task-account-input"
                    value={formData.accountName}
                    onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                    className="mt-1.5 bg-white"
                    placeholder="e.g., BharatREVOLT"
                  />
                </div>
                <div>
                  <Label htmlFor="dueDate" className="text-sm font-semibold">Due Date</Label>
                  <Input
                    id="dueDate"
                    type="date"
                    data-testid="task-due-date-input"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="status" className="text-sm font-semibold">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="task-status-select" className="mt-1.5 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="Yet to Start">Yet to Start</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Done">Done</SelectItem>
                      <SelectItem value="On Hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="priority" className="text-sm font-semibold">Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                    <SelectTrigger data-testid="task-priority-select" className="mt-1.5 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="high">🔴 High</SelectItem>
                      <SelectItem value="med">🟡 Medium</SelectItem>
                      <SelectItem value="low">🟢 Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assignedTo" className="text-sm font-semibold">Assigned To</Label>
                  <Select value={formData.assignedTo} onValueChange={(value) => setFormData({ ...formData, assignedTo: value })}>
                    <SelectTrigger data-testid="task-assigned-to-select" className="mt-1.5 bg-white">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 border-t pt-4">
                  <Label className="text-sm font-bold mb-3 block">RACI Matrix - Assign Team Members</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {['responsible', 'accountable', 'consulted', 'informed'].map((role) => (
                      <div key={role} className="border rounded-lg p-4 bg-gray-50">
                        <Label className="text-sm font-bold capitalize mb-3 block">
                          {role.charAt(0).toUpperCase()} - {role === 'responsible' && 'Does the work'}
                          {role === 'accountable' && 'Ultimately answerable'}
                          {role === 'consulted' && 'Provides input'}
                          {role === 'informed' && 'Kept updated'}
                        </Label>
                        <div className="space-y-2">
                          {teamMembers.map((member) => (
                            <label key={member.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData[role]?.includes(member.id)}
                                onChange={() => toggleMemberInRole(role, member.id)}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <span className="text-sm">{member.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="instructions" className="text-sm font-semibold">Instructions / SOP</Label>
                  <Textarea
                    id="instructions"
                    data-testid="task-instructions-textarea"
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={3}
                    className="mt-1.5 bg-white"
                    placeholder="Add detailed instructions or SOPs here..."
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="links" className="text-sm font-semibold">Links (comma separated)</Label>
                  <Input
                    id="links"
                    data-testid="task-links-input"
                    value={formData.links}
                    onChange={(e) => setFormData({ ...formData, links: e.target.value })}
                    placeholder="https://example.com, https://another.com"
                    className="mt-1.5 bg-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-btn">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-task-btn" className="bg-primary hover:bg-primary/90">
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Bulk Action Bar */}
      {selectedTaskIds.size > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200 flex items-center justify-between">
          <span className="text-sm font-medium">{selectedTaskIds.size} task(s) selected</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-slate-400 text-slate-700"
              onClick={handleBulkArchive}
              data-testid="bulk-archive-btn"
            >
              Archive Selected
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-btn"
            >
              Delete Selected
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedTaskIds(new Set())}
            >
              Clear
            </Button>
          </div>
        </Card>
      )}
      {/* Date Filter and Archive Controls */}
      <Card className="p-4 bg-white border-border/60">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Filter Buttons */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filter:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={dateFilter === 'all' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('all'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-all-btn"
            >
              All ({activeTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'today' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('today'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-today-btn"
            >
              Today ({todayTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'last-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('last-week'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-last-week-btn"
            >
              Last Week ({lastWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'this-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('this-week'); setSpecificDate(''); setShowArchived(false); }}
              className={dateFilter === 'this-week' ? 'bg-blue-600 hover:bg-blue-700' : 'text-blue-600 border-blue-300'}
              data-testid="filter-this-week-btn"
            >
              This Week ({thisWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'next-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('next-week'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-next-week-btn"
            >
              Next Week ({nextWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'overdue' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('overdue'); setSpecificDate(''); setShowArchived(false); }}
              className={dateFilter === 'overdue' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-300'}
              data-testid="filter-overdue-btn"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Overdue ({overdueTasksCount})
            </Button>
          </div>

          {/* Date Picker */}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={specificDate}
              onChange={(e) => {
                setSpecificDate(e.target.value);
                setDateFilter('specific');
              }}
              className="w-40 h-9 bg-white"
              data-testid="date-picker"
            />
            {specificDate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSpecificDate(''); setDateFilter('all'); setShowArchived(false); }}
                className="h-9 px-2"
              >
                Clear
              </Button>
            )}
          </div>
          {/* Month Picker */}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <Select
              value={selectedMonth}
              onValueChange={(v) => { setSelectedMonth(v); setDateFilter('specific-month'); }}
            >
              <SelectTrigger className="w-28 h-9 bg-white" data-testid="month-picker">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, idx) => (
                  <SelectItem key={m} value={m}>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'][idx]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedYear}
              onValueChange={(v) => { setSelectedYear(v); setDateFilter('specific-month'); }}
            >
              <SelectTrigger className="w-24 h-9 bg-white" data-testid="year-picker">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {[2025, 2026, 2027].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(selectedMonth || selectedYear) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSelectedMonth(''); setSelectedYear(''); setDateFilter('all'); setShowArchived(false); }}
                className="h-9 px-2"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Client/Account Filter */}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid="account-filter">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Clients</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Archive Toggle */}
          <div className="ml-auto flex items-center gap-2">
                          <Button
                size="sm"
                variant="outline"
                onClick={() => setExtensionRequestsOpen(true)}
                className="text-orange-700 border-orange-300 hover:bg-orange-50"
                data-testid="extension-requests-btn"
              >
                <CalendarDays className="h-4 w-4 mr-1" />
                Extension Requests ({extensionRequests.length})
              </Button>
            <Button
              size="sm"
              variant={dateFilter === 'done' && !showArchived ? 'default' : 'outline'}
              onClick={() => { setDateFilter('done'); setSpecificDate(''); setShowArchived(false); }}
              className={dateFilter === 'done' && !showArchived ? 'bg-green-600 hover:bg-green-700' : 'text-green-700 border-green-300'}
              data-testid="filter-done-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Done ({doneTasksCount})
            </Button>
            <Button
              size="sm"
              variant={showArchived ? 'default' : 'outline'}
              onClick={() => { setShowArchived(!showArchived); setDateFilter('all'); setSpecificDate(''); }}
              className={showArchived ? 'bg-slate-700' : ''}
              data-testid="toggle-archived-btn"
            >
              <Archive className="h-4 w-4 mr-1" />
              {showArchived ? 'Showing Archived' : 'Show Archived'} ({archivedTasksCount})
            </Button>
          </div>
        </div>
      </Card>

      {/* Tasks Table */}
      <Card className="border-border/60 bg-white">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={sortedTasks.length > 0 && selectedTaskIds.size === sortedTasks.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTaskIds(new Set(sortedTasks.map(t => t.id)));
                      } else {
                        setSelectedTaskIds(new Set());
                      }
                    }}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Division</th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Task</th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Account</th>
                {false && <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Type</th>}
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Assigned</th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((task) => (
                <motion.tr
                  key={task.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`border-b border-border/50 transition-colors ${getRowClassName(task)}`}
                  data-testid={`task-row-${task.id}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.has(task.id)}
                      onChange={() => toggleTaskSelection(task.id)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {task.deliverable || '-'}
                    <JobWorkCard task={task} className="mt-1" />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {task.isLagging && (
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 animate-pulse">
                          <AlertTriangle className="h-3 w-3 text-white" />
                        </span>
                      )}
                      {task.priority === 'high' && !task.isLagging && (
                        <span className="text-red-500">🔴</span>
                      )}
                      {task.title}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{task.accountName || '-'}</td>
                  {false && <td className="px-4 py-3 text-sm">{task.taskType || '-'}</td>}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>
                      {task.onHoldReason && task.status === 'On Hold' && (
                        <span className="text-xs text-red-700 italic">
                          {task.onHoldReason.substring(0, 30)}{task.onHoldReason.length > 30 ? '...' : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {task.responsible && task.responsible.length > 0
                      ? task.responsible.map(id => teamMembers.find(m => m.id === id)?.name || 'Unknown').join(', ')
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(task)}
                        className="p-1 hover:bg-muted rounded transition-colors"
                        data-testid={`edit-task-${task.id}`}
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        className="p-1 hover:bg-error/10 rounded transition-colors"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-error" />
                      </button>
                      {task.status === 'Done' && !task.archived && !task.archivedGlobally && (
                        <button
                          onClick={() => handleArchive(task)}
                          className="p-1 hover:bg-slate-100 rounded transition-colors"
                          data-testid={`archive-task-${task.id}`}
                          title="Archive task"
                        >
                          <Archive className="h-4 w-4 text-slate-600" />
                        </button>
                      )}
                      {(task.archived || task.archivedGlobally) && (
                        <button
                          onClick={() => handleUnarchive(task)}
                          className="p-1 hover:bg-blue-100 rounded transition-colors"
                          data-testid={`unarchive-task-${task.id}`}
                          title="Restore task"
                        >
                          <RotateCcw className="h-4 w-4 text-blue-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {sortedTasks.map((task) => (
            <div
              key={task.id}
              className={`p-4 rounded-lg border ${getRowClassName(task)}`}
              data-testid={`task-card-mobile-${task.id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selectedTaskIds.has(task.id)}
                  onChange={() => toggleTaskSelection(task.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap">
                    {task.isLagging && (
                      <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-500">
                        <AlertTriangle className="h-2.5 w-2.5 text-white" />
                      </span>
                    )}
                    {task.priority === 'high' && !task.isLagging && (
                      <span className="text-red-500 text-sm">🔴</span>
                    )}
                    <span className="font-semibold text-sm">{task.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{task.deliverable || '-'}</p>
                  <JobWorkCard task={task} className="mt-0.5" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(task.status)}`}>
                  {task.status}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(task.priority)}`}>
                  {task.priority}
                </span>
              </div>
              <div className="text-xs text-muted-foreground space-y-1 mb-3">
                <p>Account: {task.accountName || '-'}</p>
                <p>Due: {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : '-'}</p>
                <p>Assigned: {task.responsible && task.responsible.length > 0
                  ? task.responsible.map(id => teamMembers.find(m => m.id === id)?.name || 'Unknown').join(', ')
                  : '-'}</p>
              </div>
              <div className="flex gap-3 pt-2 border-t border-border/50">
                <button onClick={() => handleEdit(task)} className="p-1.5 hover:bg-muted rounded" data-testid={`edit-task-mobile-${task.id}`}>
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                </button>
                <button onClick={() => handleDelete(task.id)} className="p-1.5 hover:bg-error/10 rounded" data-testid={`delete-task-mobile-${task.id}`}>
                  <Trash2 className="h-4 w-4 text-error" />
                </button>
                {task.status === 'Done' && !task.archived && !task.archivedGlobally && (
                  <button onClick={() => handleArchive(task)} className="p-1.5 hover:bg-slate-100 rounded" title="Archive task">
                    <Archive className="h-4 w-4 text-slate-600" />
                  </button>
                )}
                {(task.archived || task.archivedGlobally) && (
                  <button onClick={() => handleUnarchive(task)} className="p-1.5 hover:bg-blue-100 rounded" title="Restore task">
                    <RotateCcw className="h-4 w-4 text-blue-600" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
      {/* Extension Requests Dialog */}
      <Dialog open={extensionRequestsOpen} onOpenChange={setExtensionRequestsOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Pending Extension Requests</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {extensionRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending requests.</p>
            ) : (
              extensionRequests.map((req) => (
                <div key={req.taskId} className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <p className="font-semibold">{req.taskTitle}</p>
                  <p className="text-xs text-muted-foreground mb-2">{req.accountName} • Requested by {req.requestedBy}</p>
                  <div className="text-sm mb-2">
                    <p>Current due date: <span className="font-medium">{req.currentDueDate ? format(new Date(req.currentDueDate), 'MMM dd, yyyy') : 'Not set'}</span></p>
                    <p>Requested new date: <span className="font-medium text-orange-700">{req.requestedDate ? format(new Date(req.requestedDate), 'MMM dd, yyyy') : '-'}</span></p>
                  </div>
                  <p className="text-sm text-gray-700 mb-3 italic">"{req.reason}"</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleExtensionDecision(req.taskId, 'approve', req.requestedDate)}
                    >
                      Approve as Requested
                    </Button>
                    <Input
                      type="date"
                      className="w-40 h-9 bg-white"
                      value={customApprovalDate[req.taskId] || ''}
                      onChange={(e) => setCustomApprovalDate({ ...customApprovalDate, [req.taskId]: e.target.value })}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!customApprovalDate[req.taskId]}
                      onClick={() => handleExtensionDecision(req.taskId, 'approve', customApprovalDate[req.taskId])}
                    >
                      Approve with This Date
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleExtensionDecision(req.taskId, 'reject')}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default RASheet;
